import { useState, useEffect } from 'react';
import {
  bool,
  func,
  instanceOf,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';

function DateControl(props) {
  const {
    disabled,
    events = {},
    error,
    helperText,
    label,
    name,
    settings = {},
    size,
    onChange,
    variant,
    value,
    trimestre,
    minDate,
    maxDate,
  } = props;

  const defaultDate = value ? new Date(value) : new Date();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(defaultDate);

  useEffect(() => {
    if (!value) {
      handleChange(defaultDate);
    }
  }, []);

  // Refleja cambios hechos desde fuera del control (p. ej. setFieldValue de
  // Formik); sin esto el picker seguía mostrando la fecha inicial.
  useEffect(() => {
    if (value && new Date(value).getTime() !== new Date(selected).getTime()) {
      setSelected(new Date(value));
    }
  }, [value]);

  let format = {
    year: 'YYYY',
    month: 'YYYY MMMM',
  }[settings?.minPeriod] || 'yyyy-MM-DD';

  if (settings?.hasTime) {
    format += ' HH:mm';
  }

  const openTo = {
    year: 'year',
    month: 'month',
  }[settings?.minPeriod] || 'date';

  const views = {
    year: ['year'],
    month: ['year', 'month'],
  }[settings?.minPeriod];

  const handleChange = (date) => {
    try {
      let value = new Date(date);

      if (settings?.minPeriod === 'year') {
        value = new Date(value.getFullYear(), 0, 1);
      } else if (settings?.minPeriod === 'month') {
        value = new Date(value.getFullYear(), value.getMonth(), 1);
      } else if (!settings?.hasTime) {
        value = new Date(value.getFullYear(), value.getMonth(), value.getDate());
      }

      if (settings?.onlyDate) {
        value = value.toISOString().split('T').shift();
      }

      const target = {
        name,
        value,
      };
      onChange({
        target,
      });

      if (events.onChange) {
        events.onChange(target);
      }

      setSelected(value);
    } catch (err) {
      setSelected(date);
    }
  };

  const Component = settings?.hasTime
    ? KeyboardDateTimePicker
    : KeyboardDatePicker;

  return (
    <Component
      ampm={false}
      autoOk={true}
      disabled={disabled}
      error={error}
      format={format}
      fullWidth={true}
      helperText={helperText}
      inputVariant={variant}
      InputLabelProps={{
        shrink: `${selected}` !== 'Invalid Date',
      }}
      KeyboardButtonProps={{
        'aria-label': 'change date',
      }}
      label={label}
      name={name}
      onChange={handleChange}
      onClick={() => !disabled && setOpen(true)}
      onClose={() => setOpen(false)}
      open={open}
      openTo={openTo}
      size={size}
      value={selected}
      variant="inline"
      views={views}
      minDate={minDate ? new Date(minDate) : undefined}
      maxDate={maxDate ? new Date(maxDate) : undefined}
      shouldDisableDate={(date) => {
        if (!trimestre && !minDate && !maxDate) return false; // Sin restricciones no se deshabilita ninguna fecha

        if (trimestre) {
          const varTrimestre = trimestre;
          const month = new Date(date).getMonth();

          const trimestres = {
            1: [0, 1, 2],   // Enero - Marzo
            2: [3, 4, 5],   // Abril - Junio
            3: [6, 7, 8],   // Julio - Septiembre
            4: [9, 10, 11],  // Octubre - Diciembre
          };

          return !trimestres[varTrimestre].includes(month);
        }

        if (minDate && new Date(date) < new Date(minDate)) {
          return true;
        }

        if (maxDate && new Date(date) > new Date(maxDate)) {
          return true;
        }

        return false;
      }}
    />
  );
}

DateControl.defaultProps = {
  disabled: false,
  error: false,
  onChange: () => { },
  onBlur: () => { },
  trimestre: null,
  minDate: null,
  maxDate: null,
};

DateControl.propTypes = {
  disabled: bool,
  error: bool,
  events: object,
  helperText: string,
  label: string,
  name: string,
  settings: shape({
    hasTime: bool,
    minPeriod: oneOf([
      'year',
      'month',
      'day',
    ]),
    onlyDate: bool,
  }),
  size: oneOf([
    'small',
    'medium',
  ]),
  onChange: func,
  onBlur: func,
  value: string,
  variant: oneOf([
    'outlined',
    'standard',
  ]),
  trimestre: oneOf([
    1,
    2,
    3,
    4,
  ]),
  minDate: oneOfType([
    string,
    instanceOf(Date),
  ]),
  maxDate: oneOfType([
    string,
    instanceOf(Date),
  ]),
};

export default DateControl;
